import React, { useEffect } from "react";
import styled from "@emotion/styled";
import { NavLink } from "react-router-dom";
import { Helmet } from "react-helmet-async";

import {
  Box,
  CardContent,
  CircularProgress,
  Grid,
  IconButton,
  Link,
  Breadcrumbs as MuiBreadcrumbs,
  Card as MuiCard,
  Divider as MuiDivider,
  Paper,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { spacing } from "@mui/system";
import { Print } from "@mui/icons-material";
import IncomeStatement from "./IncomeStatement";
import useAuth from "../../../hooks/useAuth";
import { fetchFinancialStatementsWithPrev } from "../../../redux/slices/accounting";
import useAppDispatch from "../../../hooks/useAppDispatch";
import useAppSelector from "../../../hooks/useAppSelector";

const Card = styled(MuiCard)(spacing);
const Divider = styled(MuiDivider)(spacing);
const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);

const PrintableContainer = styled("div")`
  .print-only {
    display: none;
  }

  @media print {
    & .no-print,
    & .MuiBreadcrumbs-root,
    & .MuiDivider-root {
      display: none !important;
    }

    .print-only {
      display: block !important;
    }

    /* Generic table print styles (apply to tables inside IncomeStatement) */
    & .MuiTableCell-root {
      border: 1px solid #000 !important;
      font-size: 10px !important;
      padding: 4px !important;
    }
    & .MuiTableHead-root .MuiTableCell-root {
      background-color: #f5f5f5 !important;
      font-weight: bold !important;
    }
  }
`;

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

function FinancialStatement() {
  const { currentUser } = useAuth();
  const dispatch = useAppDispatch();

  const associationId =
    currentUser?.activeAssociation?.id || currentUser?.associations?.[0]?.id;

  const {
    activeFiscalYear,
    incomeStatement,
    assets,
    liabilities,
    incomeStatementPrev,
    assetsPrev,
    liabilitiesPrev,
    status,
  } = useAppSelector((state) => state.accounting);
  const isReady = status === "idle" && incomeStatement && assets && liabilities;

  const [value, setValue] = React.useState(0);
  const tabLabels = ["Tuloslaskelma", "Tase vastaavaa", "Tase vastattavaa"];

  const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const handlePrint = () => {
    setTimeout(() => window.print(), 100);
  };

  useEffect(() => {
    if (!currentUser || !activeFiscalYear) return;
    dispatch(
      fetchFinancialStatementsWithPrev({
        associationId,
        from: activeFiscalYear.from,
        to: activeFiscalYear.to,
      })
    );
  }, [dispatch, currentUser, activeFiscalYear, associationId]);

  return (
    <PrintableContainer>
      <Helmet title="Blank" />

      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        className="no-print"
      >
        <Typography variant="h3" gutterBottom display="inline">
          Tulos & Tase
        </Typography>
      </Box>

      <Typography variant="h5" className="print-only" gutterBottom>
        Tulos & Tase — {tabLabels[value]}
      </Typography>

      <Breadcrumbs aria-label="Breadcrumb" mt={2} className="no-print">
        <Link component={NavLink} to="/">
          Etusivu
        </Link>
        <Link component={NavLink} to="/">
          Talous
        </Link>
        <Typography>Tulos & Tase</Typography>
      </Breadcrumbs>

      <Divider my={6} className="no-print" />

      <Grid container spacing={6}>
        <Grid item xs={12}>
          <Card mb={6}>
            <CardContent>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
              >
                <Typography variant="h6" gutterBottom sx={{ mb: 0 }}>
                  Tulos & Tase
                </Typography>
                <IconButton
                  onClick={handlePrint}
                  title="Tulosta"
                  className="no-print"
                >
                  <Print />
                </IconButton>
              </Box>

              <Paper>
                <Box sx={{ width: "100%" }}>
                  <Box
                    sx={{ borderBottom: 1, borderColor: "divider" }}
                    className="no-print"
                  >
                    <Tabs
                      value={value}
                      onChange={handleChange}
                      aria-label="financial statements tabs"
                    >
                      <Tab label="Tuloslaskelma" {...a11yProps(0)} />
                      <Tab label="Tase vastaavaa" {...a11yProps(1)} />
                      <Tab label="Tase vastattavaa" {...a11yProps(2)} />
                    </Tabs>
                  </Box>

                  {!isReady ? (
                    <Box
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      height="50vh"
                    >
                      <CircularProgress />
                    </Box>
                  ) : (
                    <>
                      <CustomTabPanel value={value} index={0}>
                        <IncomeStatement
                          rows={incomeStatement}
                          prevRows={incomeStatementPrev}
                        />
                      </CustomTabPanel>

                      <CustomTabPanel value={value} index={1}>
                        <IncomeStatement rows={assets} prevRows={assetsPrev} />
                      </CustomTabPanel>

                      <CustomTabPanel value={value} index={2}>
                        <IncomeStatement
                          rows={liabilities}
                          prevRows={liabilitiesPrev}
                        />
                      </CustomTabPanel>
                    </>
                  )}
                </Box>
              </Paper>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </PrintableContainer>
  );
}

export default FinancialStatement;
